import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { AppText, Button, Card, Badge } from "./ui";

const TA = "w-full bg-white rounded-2xl border-2 border-km-border px-4 py-3 font-text text-[16px] outline-none focus:border-km-coral resize-y";

function AlertCard({ a, onOpenRunner, reload }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(a.suggested_reply || "");
  const [reuse, setReuse] = useState(true);
  const [busy, setBusy] = useState(false);
  const who = a.runner ? `${a.runner.first_name || "—"} · ${a.runner.email}` : "coureur inconnu";
  const send = async () => {
    if (text.trim().length < 2) { toast.error("Réponse trop courte"); return; }
    setBusy(true);
    try { const r = await api.coachAlertReply(a.id, text.trim(), reuse); toast.success(r.saved ? "Réponse envoyée et mémorisée pour les cas similaires" : "Réponse envoyée"); reload(); }
    catch (e) { toast.error(e.message || "Erreur"); setBusy(false); }
  };
  return (
    <Card className={`flex flex-col gap-2 p-4 ${a.resolved ? "opacity-70" : ""}`} testID={`alert-${a.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <Badge text={a.type_label || a.type} tone={a.priority === "haute" ? "brand" : "neutral"} />
        {a.priority === "haute" ? <Badge text="priorité haute" tone="brand" /> : null}
        {a.resolved ? <Badge text={a.resolved_by === "ia" ? "Traité par l'assistant" : `Traité par ${a.resolved_by || "toi"}`} tone="neutral" /> : null}
        <AppText variant="caption" className="text-km-muted ml-auto">{a.created_at ? new Date(a.created_at).toLocaleDateString("fr-FR") : ""}</AppText>
      </div>
      {a.runner ? (
        <button type="button" onClick={() => onOpenRunner(a.runner_id)} className="text-left font-text font-bold text-[15px] text-km-coral underline-offset-2 hover:underline" data-testid={`alert-runner-${a.id}`}>{who} →</button>
      ) : <AppText variant="caption" className="text-km-muted">{who}</AppText>}
      <AppText variant="body">{a.message}</AppText>
      {a.resolved && (a.coach_reply || (a.resolved_by === "ia" && a.suggested_reply)) ? (
        <AppText variant="caption" className="text-km-ink border-l-2 border-km-sage pl-3">{a.resolved_by === "ia" ? "Réponse envoyée par l'assistant" : "Ta réponse"} : {a.coach_reply || a.suggested_reply}</AppText>
      ) : null}
      {!a.resolved ? (
        <div className="flex flex-col gap-2">
          {!open ? (
            <div className="flex flex-wrap gap-2">
              <Button title="Répondre" size="md" onClick={() => setOpen(true)} testID={`alert-reply-open-${a.id}`} />
              <Button title="Traité sans réponse" size="md" variant="secondary" onClick={() => api.coachResolveAlert(a.id).then(reload)} testID={`alert-resolve-${a.id}`} />
            </div>
          ) : (
            <>
              {a.suggested_reply ? <AppText variant="label" className="text-km-muted">Brouillon de l'assistant — relis, corrige, envoie</AppText> : null}
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} className={TA} placeholder="Ta réponse au coureur…" data-testid={`alert-reply-text-${a.id}`} />
              <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={reuse} onChange={(e) => setReuse(e.target.checked)} className="w-4 h-4 accent-km-coral" data-testid={`alert-reuse-${a.id}`} /><AppText variant="caption">Mémoriser : l'assistant répondra dans ce sens aux cas similaires</AppText></label>
              <div className="flex flex-wrap gap-2">
                <Button title={a.runner ? "Envoyer au coureur" : "Enregistrer"} size="md" onClick={send} loading={busy} testID={`alert-reply-send-${a.id}`} />
                <Button title="Annuler" size="md" variant="secondary" onClick={() => setOpen(false)} />
              </div>
            </>
          )}
        </div>
      ) : null}
    </Card>
  );
}

export function CoachQueue({ onOpenRunner }) {
  const [showAll, setShowAll] = useState(false);
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState(null);
  const load = React.useCallback(() => api.coachAlerts(showAll).then(setData).catch((e) => toast.error(e.message)), [showAll]);
  React.useEffect(() => { load(); }, [load]);
  const loadAnswers = () => api.coachAnswers().then(setAnswers).catch((e) => toast.error(e.message));
  const del = async (id) => { await api.coachDeleteAnswer(id); loadAnswers(); };
  return (
    <div className="flex flex-col gap-3" data-testid="coach-queue">
      <label className="flex items-center gap-2 cursor-pointer self-start"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="w-4 h-4 accent-km-coral" data-testid="queue-show-all" /><AppText variant="caption">Voir aussi ce qui a été traité (par toi ou par l'assistant)</AppText></label>
      {!data ? <AppText variant="caption" className="text-km-muted">Chargement…</AppText>
        : data.length === 0 ? <AppText variant="body" className="text-km-muted">Rien à regarder pour l'instant.</AppText>
        : data.map((a) => <AlertCard key={a.id} a={a} onOpenRunner={onOpenRunner} reload={load} />)}
      <div className="mt-4">
        <button type="button" onClick={() => (answers ? setAnswers(null) : loadAnswers())} className="font-text font-bold text-[15px] text-km-ink" data-testid="answers-toggle">{answers ? "− " : "+ "}Tes réponses mémorisées (réutilisées par l'assistant)</button>
        {answers ? (
          <div className="flex flex-col divide-y divide-km-divider mt-2" data-testid="answers-list">
            {answers.length === 0 ? <AppText variant="caption" className="text-km-muted py-2">Aucune pour l'instant : réponds à une alerte avec « Mémoriser » coché.</AppText> : answers.map((x) => (
              <div key={x.id} className="py-2 flex gap-3 items-start" data-testid={`answer-${x.id}`}>
                <div className="flex-1 min-w-0">
                  <AppText variant="caption" className="text-km-muted">{x.situation}</AppText>
                  <AppText variant="caption" className="text-km-ink">→ {x.answer}</AppText>
                </div>
                <button type="button" onClick={() => del(x.id)} className="text-km-muted text-sm font-bold" data-testid={`answer-delete-${x.id}`}>Supprimer</button>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
